"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronUp, CreditCard, ExternalLink, LogOut } from "lucide-react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { SidebarLoungePoints } from "@/components/lounge/sidebar-points";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { Wordmark } from "@/components/ui/wordmark";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { withOrgParam } from "@/lib/utils";
import { OrganizationSwitcher } from "./organization-switcher";
import { SidebarChatSearch, SidebarNewAction } from "./sidebar-actions";
import { StudioNav } from "./studio-nav";
import type { Organization } from "@/lib/types";
interface CanvasSidebarProps {
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (organization: Organization | null) => void;
  className?: string;
  onNewCanvas?: () => void;
}
export function CanvasSidebar(t0) {
  const $ = _c(109);
  const {
    organizations,
    selectedOrganization,
    onSelectOrganization,
    className,
    onNewCanvas
  } = t0;
  let switcherOrganizations;
  let t1;
  if ($[0] !== organizations || $[1] !== selectedOrganization?.id) {
    switcherOrganizations = organizations.filter(_temp);
    t1 = switcherOrganizations.find(org_0 => org_0.id === selectedOrganization?.id) ?? null;
    $[0] = organizations;
    $[1] = selectedOrganization?.id;
    $[2] = switcherOrganizations;
    $[3] = t1;
  } else {
    switcherOrganizations = $[2];
    t1 = $[3];
  }
  const switcherSelectedOrganization = t1;
  const router = useRouter();
  const posthog = usePostHog();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const {
    organization,
    isLoading: isOrgLoading
  } = useOrganization();
  let t2;
  if ($[4] !== posthog || $[5] !== router || $[6] !== signOut) {
    t2 = async () => {
      posthog.reset();
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {}
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : "https://chat.llmgateway.io/login");
          }
        }
      });
    };
    $[4] = posthog;
    $[5] = router;
    $[6] = signOut;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const logout = t2;
  const searchParams = useSearchParams();
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let T5;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[8] !== className || $[9] !== isUserLoading || $[10] !== searchParams || $[11] !== user) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const orgIdParam = searchParams.get("orgId");
      const withOrg = path => withOrgParam(path, orgIdParam);
      const isAuthenticated = !!user;
      if (isUserLoading) {
        let t10;
        if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4"><Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}><Wordmark /><Badge>Canvas</Badge></Link></div><StudioNav /></SidebarHeader>;
          $[25] = t10;
        } else {
          t10 = $[25];
        }
        let t11;
        if ($[26] !== className) {
          t11 = <Sidebar className={className}>{t10}</Sidebar>;
          $[26] = className;
          $[27] = t11;
        } else {
          t11 = $[27];
        }
        t9 = t11;
        break bb0;
      }
      if (!isAuthenticated) {
        let t10;
        if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = <Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}><Wordmark /><Badge>Canvas</Badge></Link>;
          $[28] = t10;
        } else {
          t10 = $[28];
        }
        let t11;
        let t12;
        if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = <div className="font-medium mb-2">Sign in required</div>;
          t12 = <p className="text-muted-foreground mb-3">Please sign in to use Canvas.</p>;
          $[29] = t11;
          $[30] = t12;
        } else {
          t11 = $[29];
          t12 = $[30];
        }
        let t13;
        if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
          t13 = <Button size="sm" asChild={true}><Link href="/login">Sign in</Link></Button>;
          $[31] = t13;
        } else {
          t13 = $[31];
        }
        let t14;
        if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
          t14 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t10}<div className="w-full rounded-md border p-4 text-sm">{t11}{t12}<div className="flex items-center justify-end gap-2">{t13}<Button size="sm" variant="outline" asChild={true}><Link href="/signup">Create account</Link></Button></div></div></div><StudioNav /></SidebarHeader>;
          $[32] = t14;
        } else {
          t14 = $[32];
        }
        let t15;
        if ($[33] !== className) {
          t15 = <Sidebar className={className}>{t14}</Sidebar>;
          $[33] = className;
          $[34] = t15;
        } else {
          t15 = $[34];
        }
        t9 = t15;
        break bb0;
      }
      T5 = Sidebar;
      t7 = "icon";
      t8 = (className ?? "") + " max-md:hidden";
      T4 = SidebarHeader;
      T3 = SidebarMenu;
      T2 = SidebarMenuItem;
      T1 = SidebarMenuButton;
      t4 = "lg";
      t5 = true;
      t6 = "Lounge";
      T0 = Link;
      t3 = withOrg("/");
    }
    $[8] = className;
    $[9] = isUserLoading;
    $[10] = searchParams;
    $[11] = user;
    $[12] = T0;
    $[13] = T1;
    $[14] = T2;
    $[15] = T3;
    $[16] = T4;
    $[17] = T5;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    T0 = $[12];
    T1 = $[13];
    T2 = $[14];
    T3 = $[15];
    T4 = $[16];
    T5 = $[17];
    t3 = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
    t8 = $[23];
    t9 = $[24];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Wordmark size="sm" iconBox={true} />;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== T0 || $[37] !== t3) {
    t11 = <T0 href={t3} prefetch={true}>{t10}</T0>;
    $[36] = T0;
    $[37] = t3;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== T1 || $[40] !== t11 || $[41] !== t4 || $[42] !== t5 || $[43] !== t6) {
    t12 = <T1 size={t4} asChild={t5} tooltip={t6}>{t11}</T1>;
    $[39] = T1;
    $[40] = t11;
    $[41] = t4;
    $[42] = t5;
    $[43] = t6;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== T2 || $[46] !== t12) {
    t13 = <T2>{t12}</T2>;
    $[45] = T2;
    $[46] = t12;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <SidebarChatSearch disabled={true} />;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] !== onNewCanvas) {
    t15 = <SidebarNewAction label="New Canvas" onAction={onNewCanvas} />;
    $[49] = onNewCanvas;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] !== T3 || $[52] !== t13 || $[53] !== t15) {
    t16 = <T3>{t13}{t14}{t15}</T3>;
    $[51] = T3;
    $[52] = t13;
    $[53] = t15;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  let t17;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <StudioNav />;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  let t18;
  if ($[56] !== T4 || $[57] !== t16) {
    t18 = <T4>{t16}{t17}</T4>;
    $[56] = T4;
    $[57] = t16;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] !== onSelectOrganization || $[60] !== switcherOrganizations || $[61] !== switcherSelectedOrganization) {
    t19 = switcherOrganizations.length > 0 ? <SidebarMenu className="group-data-[collapsible=icon]:hidden"><SidebarMenuItem><OrganizationSwitcher organizations={switcherOrganizations} selectedOrganization={switcherSelectedOrganization} onSelectOrganization={onSelectOrganization} /></SidebarMenuItem></SidebarMenu> : null;
    $[59] = onSelectOrganization;
    $[60] = switcherOrganizations;
    $[61] = switcherSelectedOrganization;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  let t20;
  if ($[63] !== t19) {
    t20 = <SidebarContent className="px-2 py-4">{t19}</SidebarContent>;
    $[63] = t19;
    $[64] = t20;
  } else {
    t20 = $[64];
  }
  let t21;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <SidebarLoungePoints />;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  const t22 = switcherSelectedOrganization ?? organization;
  const t23 = !switcherSelectedOrganization;
  let t24;
  if ($[66] !== isOrgLoading || $[67] !== t22 || $[68] !== t23) {
    t24 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={t22} isLoading={isOrgLoading} isChatPlanOrg={t23} /></div>;
    $[66] = isOrgLoading;
    $[67] = t22;
    $[68] = t23;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  const t25 = user?.name ?? "User";
  let t26;
  if ($[70] !== user?.name) {
    t26 = user?.name?.split(" ").map(_temp2).join("").toUpperCase().slice(0, 2) ?? "U";
    $[70] = user?.name;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== t26) {
    t27 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t26}</span></div>;
    $[72] = t26;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  const t28 = user?.name;
  let t29;
  if ($[74] !== t28) {
    t29 = <span className="truncate font-semibold">{t28}</span>;
    $[74] = t28;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  const t30 = user?.email;
  let t31;
  if ($[76] !== t30) {
    t31 = <span className="truncate text-xs text-muted-foreground">{t30}</span>;
    $[76] = t30;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  let t32;
  if ($[78] !== t29 || $[79] !== t31) {
    t32 = <div className="grid flex-1 text-left text-sm leading-tight">{t29}{t31}</div>;
    $[78] = t29;
    $[79] = t31;
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  let t33;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />;
    $[81] = t33;
  } else {
    t33 = $[81];
  }
  let t34;
  if ($[82] !== t25 || $[83] !== t27 || $[84] !== t32) {
    t34 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t25} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t27}{t32}{t33}</SidebarMenuButton></DropdownMenuTrigger>;
    $[82] = t25;
    $[83] = t27;
    $[84] = t32;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  let t35;
  let t36;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <DropdownMenuItem asChild={true}><Link href="/pricing" prefetch={true}><CreditCard className="mr-2 h-4 w-4" />{"Membership & Billing"}</Link></DropdownMenuItem>;
    t36 = <DropdownMenuSeparator />;
    $[86] = t35;
    $[87] = t36;
  } else {
    t35 = $[86];
    t36 = $[87];
  }
  let t37;
  let t38;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <DropdownMenuItem asChild={true}><a href={process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : "https://llmgateway.io/dashboard"} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Dashboard</a></DropdownMenuItem>;
    t38 = <DropdownMenuSeparator />;
    $[88] = t37;
    $[89] = t38;
  } else {
    t37 = $[88];
    t38 = $[89];
  }
  let t39;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <span>Theme</span>;
    $[90] = t39;
  } else {
    t39 = $[90];
  }
  let t40;
  let t41;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <DropdownMenuItem className="justify-between gap-3" onSelect={_temp3}>{t39}<div onClick={_temp4} onKeyDown={_temp5}><ThemeToggle className="shrink-0" size="compact" /></div></DropdownMenuItem>;
    t41 = <DropdownMenuSeparator />;
    $[91] = t40;
    $[92] = t41;
  } else {
    t40 = $[91];
    t41 = $[92];
  }
  let t42;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <LogOut className="mr-2 h-4 w-4" />;
    $[93] = t42;
  } else {
    t42 = $[93];
  }
  let t43;
  if ($[94] !== logout) {
    t43 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t35}{t36}{t37}{t38}{t40}{t41}<DropdownMenuItem onClick={logout}>{t42}Log out</DropdownMenuItem></DropdownMenuContent>;
    $[94] = logout;
    $[95] = t43;
  } else {
    t43 = $[95];
  }
  let t44;
  if ($[96] !== t34 || $[97] !== t43) {
    t44 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t34}{t43}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[96] = t34;
    $[97] = t43;
    $[98] = t44;
  } else {
    t44 = $[98];
  }
  let t45;
  if ($[99] !== t24 || $[100] !== t44) {
    t45 = <SidebarFooter>{t21}{t24}{t44}</SidebarFooter>;
    $[99] = t24;
    $[100] = t44;
    $[101] = t45;
  } else {
    t45 = $[101];
  }
  let t46;
  if ($[102] !== T5 || $[103] !== t18 || $[104] !== t20 || $[105] !== t45 || $[106] !== t7 || $[107] !== t8) {
    t46 = <T5 collapsible={t7} className={t8}>{t18}{t20}{t45}</T5>;
    $[102] = T5;
    $[103] = t18;
    $[104] = t20;
    $[105] = t45;
    $[106] = t7;
    $[107] = t8;
    $[108] = t46;
  } else {
    t46 = $[108];
  }
  return t46;
}
function _temp5(event_1) {
  return event_1.stopPropagation();
}
function _temp4(event_0) {
  return event_0.stopPropagation();
}
function _temp3(event) {
  return event.preventDefault();
}
function _temp2(n) {
  return n[0];
}
function _temp(org) {
  return org.kind === "default";
}